import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Search, DollarSign, FileText, CheckCircle, XCircle,
  TrendingUp, Calendar, Building2, Download, Filter
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const AccountingPage = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  
  // Invoice dialog
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [updatingInvoice, setUpdatingInvoice] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, []);

  useEffect(() => {
    filterPayments();
  }, [payments, searchTerm, invoiceFilter, dateFilter]);

  const fetchPayments = async () => {
    try {
      const [paymentsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/admin/payments`, { withCredentials: true }),
        axios.get(`${API}/admin/bookings`, { withCredentials: true })
      ]);
      
      // Enrich payments with invoice status from bookings
      const enrichedPayments = paymentsRes.data.map(payment => {
        const booking = bookingsRes.data.find(b => b.booking_id === payment.booking_id);
        return {
          ...payment,
          invoice_issued: booking?.invoice_issued || false,
          invoice_number: booking?.invoice_number || null,
          invoice_date: booking?.invoice_date || null
        };
      });
      
      setPayments(enrichedPayments);
      setLoading(false);
    } catch (error) {
      console.error('Fetch payments error:', error);
      toast.error('Veriler yüklenirken hata oluştu');
      setLoading(false);
    }
  };

  const filterPayments = () => {
    let filtered = [...payments];

    // Search
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.payment_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.booking_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.facility_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Invoice filter
    if (invoiceFilter === 'issued') {
      filtered = filtered.filter(p => p.invoice_issued === true);
    } else if (invoiceFilter === 'not_issued') {
      filtered = filtered.filter(p => p.invoice_issued === false);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(p => {
        if (!p.booking_date) return false;
        const paymentDate = new Date(p.booking_date);
        
        switch (dateFilter) {
          case 'today':
            return paymentDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return paymentDate >= weekAgo;
          case 'month':
            return paymentDate.getMonth() === now.getMonth() && paymentDate.getFullYear() === now.getFullYear();
          default:
            return true;
        }
      });
    }

    setFilteredPayments(filtered);
  };

  const handleToggleInvoice = async (payment) => {
    setUpdatingInvoice(true);
    try {
      const newStatus = !payment.invoice_issued;
      const invoiceData = {
        invoice_issued: newStatus,
        invoice_number: newStatus ? `INV-${Date.now()}` : null,
        invoice_date: newStatus ? new Date().toISOString() : null
      };
      
      await axios.put(
        `${API}/admin/bookings/${payment.booking_id}/invoice`,
        invoiceData,
        { withCredentials: true }
      );
      
      toast.success(newStatus ? 'Fatura kaydedildi' : 'Fatura iptal edildi');
      fetchPayments();
    } catch (error) {
      console.error('Invoice update error:', error);
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setUpdatingInvoice(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const paidPayments = payments.filter(p => p.status === 'PAID');
  const totalRevenue = paidPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const invoicedPayments = payments.filter(p => p.invoice_issued === true);
  const pendingInvoices = payments.filter(p => p.invoice_issued === false && p.status === 'PAID');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Muhasebe & Kasa</h1>
          <p className="text-sm text-slate-600 mt-1">
            Gelir takibi ve fatura yönetimi
          </p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700">
          <Download className="w-4 h-4 mr-2" />
          Excel İndir
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-600">Toplam Gelir</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {totalRevenue.toLocaleString('tr-TR')} ₺
                </p>
                <p className="text-xs text-slate-500 mt-1">{paidPayments.length} ödeme</p>
              </div>
              <DollarSign className="w-10 h-10 text-emerald-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-600">Faturalandı</p>
                <p className="text-2xl font-bold text-green-700 mt-1">
                  {invoicedPayments.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString('tr-TR')} ₺
                </p>
                <p className="text-xs text-green-600 mt-1">{invoicedPayments.length} fatura</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-600">Fatura Bekliyor</p>
                <p className="text-2xl font-bold text-orange-700 mt-1">
                  {pendingInvoices.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString('tr-TR')} ₺
                </p>
                <p className="text-xs text-orange-600 mt-1">{pendingInvoices.length} ödeme</p>
              </div>
              <XCircle className="w-10 h-10 text-orange-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-600">Fatura Oranı</p>
                <p className="text-2xl font-bold text-blue-700 mt-1">
                  {paidPayments.length > 0 ? Math.round((invoicedPayments.length / paidPayments.length) * 100) : 0}%
                </p>
                <p className="text-xs text-blue-600 mt-1">Tamamlanma oranı</p>
              </div>
              <FileText className="w-10 h-10 text-blue-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Ödeme ID, rezervasyon kodu, tesis veya kullanıcı..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Invoice Filter */}
            <Select value={invoiceFilter} onValueChange={setInvoiceFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Fatura Durumu" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="issued">Fatura Kesildi</SelectItem>
                <SelectItem value="not_issued">Fatura Bekliyor</SelectItem>
              </SelectContent>
            </Select>

            {/* Date Filter */}
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Tarih" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Zamanlar</SelectItem>
                <SelectItem value="today">Bugün</SelectItem>
                <SelectItem value="week">Bu Hafta</SelectItem>
                <SelectItem value="month">Bu Ay</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarih</TableHead>
                <TableHead>Rezervasyon Kodu</TableHead>
                <TableHead>Tesis</TableHead>
                <TableHead>Kullanıcı</TableHead>
                <TableHead>Tutar</TableHead>
                <TableHead>Ödeme Durumu</TableHead>
                <TableHead>Fatura Durumu</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    Ödeme bulunamadı
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayments.map((payment) => (
                  <TableRow key={payment.payment_id}>
                    <TableCell className="text-sm">
                      {payment.booking_date || '-'}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {payment.booking_code || '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1">
                        <Building2 className="w-3 h-3 text-slate-400" />
                        {payment.facility_name || '-'}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {payment.user_name || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 font-semibold text-emerald-700">
                        <DollarSign className="w-3 h-3" />
                        {payment.amount.toLocaleString('tr-TR')} ₺
                      </div>
                    </TableCell>
                    <TableCell>
                      {payment.status === 'PAID' ? (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Ödendi
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-700">
                          {payment.status}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={payment.invoice_issued}
                          onCheckedChange={() => handleToggleInvoice(payment)}
                          disabled={updatingInvoice}
                        />
                        <span className="text-sm">
                          {payment.invoice_issued ? (
                            <span className="text-green-600 font-medium">Kesildi</span>
                          ) : (
                            <span className="text-orange-600">Bekliyor</span>
                          )}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {payment.invoice_issued && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowInvoiceDialog(true);
                          }}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Detay
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Invoice Detail Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Fatura Detayları
            </DialogTitle>
          </DialogHeader>
          
          {selectedPayment && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-600">Fatura No</p>
                  <p className="font-mono font-medium">{selectedPayment.invoice_number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Fatura Tarihi</p>
                  <p className="font-medium">
                    {selectedPayment.invoice_date 
                      ? format(new Date(selectedPayment.invoice_date), 'd MMMM yyyy', { locale: tr })
                      : '-'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Rezervasyon Kodu</p>
                  <p className="font-mono">{selectedPayment.booking_code}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Tesis</p>
                  <p className="font-medium">{selectedPayment.facility_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Müşteri</p>
                  <p className="font-medium">{selectedPayment.user_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Tutar</p>
                  <p className="font-bold text-lg text-emerald-700">{selectedPayment.amount.toLocaleString('tr-TR')} ₺</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountingPage;
